import React from "react";
import {
  channelLabel,
  channelNoneLabel,
  channelsInOrder,
  isActive,
  isMulti
} from "./lib/overlays";

// Radio within a channel, channels independent. Nothing here knows the name of
// a single overlay: the grouping comes from the `channel` column, so a
// fourteenth overlay is a seed row rather than a UI edit.
export default function LensPicker({
  overlays,
  tradition,
  lenses,
  channelParams,
  multiChannels,
  onToggle,
  onClear,
  onClearChannel
}) {
  // Order comes from the payload's channel list, so a channel added in Ruby
  // appears here without a UI edit — which is the whole point of `channel`
  // being a column. A channel with no overlays in THIS tradition is dropped.
  const byChannel = channelsInOrder(channelParams)
    .map((channel) => ({
      channel,
      members: overlays.filter((o) => o.channel === channel)
    }))
    .filter(({ members }) => members.length > 0);

  if (byChannel.length === 0) return null;

  const anyActive = Object.keys(lenses).length > 0;

  return (
    <div className="lenses" data-testid="lens-picker">
      <div className="lenses__head">
        <h2 className="lenses__heading">Lenses</h2>
        {/* Mounted always, disabled when there is nothing to clear. It used to
            be conditionally rendered, so activating it unmounted it and threw
            keyboard focus to <body> — a reader was dumped at the top of the
            document and had to tab back through the whole nav. It also stops
            the button appearing and disappearing under the pointer. */}
        <button
          type="button"
          className="lenses__clear"
          onClick={onClear}
          disabled={!anyActive}
          data-testid="lens-clear"
        >
          Turn every lens off
        </button>
      </div>

      {byChannel.map(({ channel, members }) => {
        const multi = isMulti(channel, multiChannels);
        const anyOn = members.some((o) => isActive(lenses, o, multiChannels));
        return (
          <fieldset className="lens-group" key={channel} data-testid={`channel-${channel}`}>
            <legend className="lens-group__legend">{channelLabel(channel)}</legend>

            {/* An explicit None, selected by default. A radio cannot be unset,
                so without one "Clear all" was the only way out of a sequence.
                The marker channel is checkboxes, where unchecked already is
                none. */}
            {!multi && (
              <label className="lens lens--none">
                <input
                  type="radio"
                  name={`channel-${channel}`}
                  value=""
                  checked={!anyOn}
                  onChange={() => onClearChannel(channel)}
                  data-testid={`lens-none-${channel}`}
                />
                <span className="lens__body">
                  <span className="lens__name">{channelNoneLabel(channel)}</span>
                </span>
              </label>
            )}

            {members.map((overlay) => {
              const active = isActive(lenses, overlay, multiChannels);
              return (
                // The link is a SIBLING of the label, not a child of it. A
                // <label> may not contain interactive content, and the
                // stopPropagation this used to need was the symptom: without
                // it, following the link also toggled the lens.
                <div className="lens-row" key={overlay.slug}>
                  <label className={active ? "lens lens--on" : "lens"}>
                    <input
                      type={multi ? "checkbox" : "radio"}
                      name={`channel-${channel}`}
                      value={overlay.slug}
                      checked={active}
                      onChange={() => onToggle(overlay)}
                      onClick={() => {
                        if (!multi && active) onToggle(overlay);
                      }}
                      data-testid={`lens-${overlay.slug}`}
                    />
                    <span className="lens__body">
                      <span className="lens__name">{overlay.name}</span>
                      {/* Only the selected option explains itself. Six lenses
                          each carrying three lines filled the whole viewport. */}
                      {active && <span className="lens__summary">{overlay.summary}</span>}
                    </span>
                  </label>
                  {active && tradition && (
                    // Same tab: this is an internal page, and the lens state is
                    // already in the URL, so coming back costs nothing.
                    <a
                      className="lens__details"
                      href={`/tree/${tradition.param}/lenses/${overlay.slug}`}
                      data-testid={`lens-details-${overlay.slug}`}
                    >
                      What this lens shows
                    </a>
                  )}
                </div>
              );
            })}
          </fieldset>
        );
      })}
    </div>
  );
}
